import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { apiRequest } from '../../authConfig';
import { createRulesApi } from '../../flux/facades/rulesApi';
import { useAppSelector } from '../../flux/stores/hooks';
import { useApiAccessToken } from '../../utils/useApiAccessToken';
import { InterferenceRuleForm } from './InterferenceRule/InterferenceRuleForm';
import { apiErrorDetails, detectionLabel, errorMessage } from './ruleHelpers';

const AUTHOR_LICENSES = new Set(['author', 'pro']);

export const Rules = () => {
    const { instance } = useMsal();
    const { getAccessToken } = useApiAccessToken();
    const rulesApi = useMemo(() => createRulesApi(getAccessToken), [getAccessToken]);
    const currentUser = useAppSelector((state) => state.user.current.data);
    const loadGeneration = useRef(0);

    const [rules, setRules] = useState([]);
    const [activeRuleIds, setActiveRuleIds] = useState(() => new Set());
    const [view, setView] = useState('list');
    const [editingRule, setEditingRule] = useState(null);
    const [ruleToDelete, setRuleToDelete] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [pendingAction, setPendingAction] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [pageError, setPageError] = useState(null);
    const [formError, setFormError] = useState(null);
    const [notice, setNotice] = useState(null);
    const [sessionExpired, setSessionExpired] = useState(false);

    const isOrganizationMember = Boolean(currentUser?.org_object_id);
    const canAuthorRules = isOrganizationMember && AUTHOR_LICENSES.has(currentUser?.license_tier);
    const canAdministerRules = isOrganizationMember && currentUser?.org_role === 'admin';

    const loadRules = useCallback(async ({ showLoading = true } = {}) => {
        const generation = ++loadGeneration.current;
        if (showLoading) setIsLoading(true);
        setPageError(null);

        try {
            const [allRules, activeRules] = await Promise.all([
                rulesApi.getRules(),
                rulesApi.getActiveRules(),
            ]);
            if (generation !== loadGeneration.current) return;
            setRules(allRules);
            setActiveRuleIds(new Set(activeRules.map((rule) => rule.object_id)));
            setSessionExpired(false);
        } catch (error) {
            if (generation !== loadGeneration.current) return;
            const details = apiErrorDetails(error);
            setSessionExpired(details.status === 401);
            setPageError(errorMessage(error, 'load'));
        } finally {
            if (generation === loadGeneration.current) setIsLoading(false);
        }
    }, [rulesApi]);

    useEffect(() => {
        loadRules();
        return () => { loadGeneration.current += 1; };
    }, [loadRules]);

    const refreshAfterMissingRule = async () => {
        setView('list');
        setEditingRule(null);
        setRuleToDelete(null);
        setNotice({ type: 'warning', message: 'This rule no longer exists. The list has been refreshed.' });
        await loadRules({ showLoading: false });
    };

    const openCreate = () => {
        setFormError(null);
        setNotice(null);
        setView('create');
    };

    const openEdit = async (ruleId) => {
        setPendingAction(`edit:${ruleId}`);
        setNotice(null);
        try {
            const rule = await rulesApi.getRule(ruleId);
            setEditingRule(rule);
            setFormError(null);
            setView('edit');
        } catch (error) {
            if (apiErrorDetails(error).status === 404) {
                await refreshAfterMissingRule();
            } else {
                setSessionExpired(apiErrorDetails(error).status === 401);
                setPageError(errorMessage(error, 'open'));
            }
        } finally {
            setPendingAction(null);
        }
    };

    const handleCreate = async ({ input }) => {
        setIsSubmitting(true);
        setFormError(null);
        try {
            await rulesApi.createInterferenceRule(input);
            setView('list');
            setNotice({ type: 'success', message: 'Rule created.' });
            await loadRules({ showLoading: false });
        } catch (error) {
            setSessionExpired(apiErrorDetails(error).status === 401);
            setFormError(errorMessage(error, 'create'));
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleUpdate = async ({ input }) => {
        if (!editingRule) return;
        setIsSubmitting(true);
        setFormError(null);
        try {
            await rulesApi.updateInterferenceRule(editingRule.object_id, input);
            setView('list');
            setEditingRule(null);
            setNotice({ type: 'success', message: 'Rule updated.' });
            await loadRules({ showLoading: false });
        } catch (error) {
            if (apiErrorDetails(error).status === 404) {
                await refreshAfterMissingRule();
            } else {
                setSessionExpired(apiErrorDetails(error).status === 401);
                setFormError(errorMessage(error, 'update'));
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const confirmDelete = async () => {
        if (!ruleToDelete) return;
        const rule = ruleToDelete;
        const wasActive = activeRuleIds.has(rule.object_id);
        let wasDeactivated = false;
        setPendingAction(`delete:${rule.object_id}`);
        setNotice(null);

        try {
            if (wasActive) {
                await rulesApi.deactivateRule(rule.object_id);
                wasDeactivated = true;
            }
            await rulesApi.deleteRule(rule.object_id);
            setRuleToDelete(null);
            setNotice({ type: 'success', message: 'Rule deleted.' });
            await loadRules({ showLoading: false });
        } catch (error) {
            const status = apiErrorDetails(error).status;
            setRuleToDelete(null);
            if (status === 404) await refreshAfterMissingRule();
            else {
                setSessionExpired(status === 401);
                setPageError(errorMessage(error, 'delete'));
                if (wasDeactivated) await loadRules({ showLoading: false });
            }
        } finally {
            setPendingAction(null);
        }
    };

    const signInAgain = async () => {
        const account = instance.getActiveAccount();
        await instance.acquireTokenRedirect({ ...apiRequest, account, prompt: 'login' });
    };

    if (view === 'create') {
        return (
            <div className="page-content">
                <InterferenceRuleForm
                    onSubmit={handleCreate}
                    onCancel={() => setView('list')}
                    isSubmitting={isSubmitting}
                    serverError={formError}
                    sessionExpired={sessionExpired}
                    onSignInAgain={signInAgain}
                />
            </div>
        );
    }

    if (view === 'edit' && editingRule) {
        return (
            <div className="page-content">
                <InterferenceRuleForm
                    mode="edit"
                    initialRule={editingRule}
                    onSubmit={handleUpdate}
                    onCancel={() => { setView('list'); setEditingRule(null); }}
                    isSubmitting={isSubmitting}
                    serverError={formError}
                    sessionExpired={sessionExpired}
                    onSignInAgain={signInAgain}
                />
            </div>
        );
    }

    return (
        <div className="page-content">
            <div className="rules-header">
                <div>
                    <h1 className="rules-title">Interference Rules</h1>
                    <p className="rules-page-subtitle">Manage clash and clearance checks for your organization.</p>
                </div>
                {canAuthorRules && <button className="rules-btn-primary" onClick={openCreate}>+ New Rule</button>}
            </div>

            {notice && <div className={`rules-alert rules-alert-${notice.type}`} role="status">{notice.message}</div>}
            {pageError && (
                <div className="rules-alert rules-alert-error" role="alert">
                    <span>{pageError}</span>
                    <button type="button" className="rules-btn-ghost" onClick={sessionExpired ? signInAgain : () => loadRules()}>
                        {sessionExpired ? 'Sign in again' : 'Retry'}
                    </button>
                </div>
            )}

            {isLoading && rules.length === 0 ? (
                <p className="projects-loading">Loading interference rules…</p>
            ) : rules.length === 0 ? (
                <div className="rules-empty"><p>No interference rules yet.</p></div>
            ) : (
                <div className="rules-table-wrapper rules-management-table-wrapper">
                    <table className="rules-table rules-management-table">
                        <thead>
                            <tr>
                                <th className="rules-th">Name</th>
                                <th className="rules-th">First entity</th>
                                <th className="rules-th">Second entity</th>
                                <th className="rules-th">Effect</th>
                                <th className="rules-th">Detection</th>
                                {(canAuthorRules || canAdministerRules) && <th className="rules-th">Actions</th>}
                            </tr>
                        </thead>
                        <tbody>
                            {rules.map((rule) => {
                                const rowBusy = pendingAction?.endsWith(`:${rule.object_id}`);
                                return (
                                    <tr key={rule.object_id} className="rules-tr">
                                        <td className="rules-td rules-td-name">{rule.name}</td>
                                        <td className="rules-td">{rule.definition.firstEntity.entityType}</td>
                                        <td className="rules-td">{rule.definition.secondEntity?.entityType ?? '—'}</td>
                                        <td className="rules-td"><span className={`rules-effect rules-effect-${rule.effect}`}>{rule.effect}</span></td>
                                        <td className="rules-td rules-detection-cell">{detectionLabel(rule)}</td>
                                        {(canAuthorRules || canAdministerRules) && (
                                            <td className="rules-td">
                                                <div className="rules-row-actions">
                                                    {canAuthorRules && (
                                                        <button type="button" className="rules-action-button" disabled={rowBusy} onClick={() => openEdit(rule.object_id)}>
                                                            {pendingAction === `edit:${rule.object_id}` ? 'Opening…' : 'Edit'}
                                                        </button>
                                                    )}
                                                    {canAdministerRules && (
                                                        <button type="button" className="rules-action-button rules-action-danger" disabled={rowBusy} onClick={() => setRuleToDelete(rule)}>
                                                            Delete
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        )}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {ruleToDelete && (
                <div className="members-modal-backdrop" role="presentation">
                    <div className="members-modal" role="dialog" aria-modal="true" aria-labelledby="delete-rule-title">
                        <h2 id="delete-rule-title">Delete {ruleToDelete.name}?</h2>
                        <p>
                            {activeRuleIds.has(ruleToDelete.object_id)
                                ? 'This active rule will be deactivated and then permanently deleted.'
                                : 'This rule will be permanently deleted.'}
                        </p>
                        <div className="rules-dialog-actions">
                            <button type="button" className="rules-btn-ghost" onClick={() => setRuleToDelete(null)} disabled={pendingAction?.startsWith('delete:')}>Cancel</button>
                            <button type="button" className="rules-delete-button" onClick={confirmDelete} disabled={pendingAction?.startsWith('delete:')}>
                                {pendingAction?.startsWith('delete:') ? 'Deleting…' : 'Delete rule'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
